export type CourseClaimError = 'already_linked' | 'not_enrolled' | 'invalid_claim' | 'unavailable';

/**
 * What `POST /api/course-entitlement/handoff` puts in `?course_claim=` before the
 * student is signed in. `pending` means the token is parked in its cookie and the
 * claim still has to be finished; the rest are refusals to show right away.
 */
export type CourseClaimHandoff = 'pending' | 'invalid' | 'expired' | 'unavailable';

const HANDOFF_STATUSES: readonly string[] = ['pending', 'invalid', 'expired', 'unavailable'];

export function isCourseClaimHandoff(value: string | null): value is CourseClaimHandoff {
  return value !== null && HANDOFF_STATUSES.includes(value);
}

/**
 * Where the handoff outcome waits while the student signs in. It is the status, never
 * the token — that stays in an HttpOnly cookie — and it has to survive the trip through
 * the login page, which navigation state does not.
 */
export const COURSE_CLAIM_STATUS_KEY = 'creator-course-claim-status';

export function readCourseClaimStatus(): CourseClaimHandoff | null {
  try {
    const status = sessionStorage.getItem(COURSE_CLAIM_STATUS_KEY);
    return isCourseClaimHandoff(status) ? status : null;
  } catch {
    // Private mode, blocked site data: "no handoff waiting" is a fine answer.
    return null;
  }
}

export function storeCourseClaimStatus(status: CourseClaimHandoff): void {
  try {
    sessionStorage.setItem(COURSE_CLAIM_STATUS_KEY, status);
  } catch {
    // Without storage the student simply sees nothing after the login page.
  }
}

export function clearCourseClaimStatus(): void {
  try {
    sessionStorage.removeItem(COURSE_CLAIM_STATUS_KEY);
  } catch {
    // Nothing was stored, so nothing has to be removed.
  }
}

/**
 * The question asked before a course account is tied to a Creator account.
 *
 * It names the Creator account on purpose. Anyone enrolled on the course can get
 * someone else's browser to post their claim to the handoff; redeemed silently, that
 * would bind the victim's account to the attacker's course account for good — 409 on
 * every later attempt, and the attacker able to revoke the plan. Seeing whose account
 * is about to be linked, and having to agree, is what closes that.
 */
export function courseClaimPromptText(lang: 'ru' | 'en', account: string, alreadyConnected = false): {
  question: string; note: string; confirm: string; cancel: string;
} {
  return lang === 'en'
    ? {
      // A student whose plan is already connected is confirming again, not connecting.
      // Asking as though nothing were connected reads as if the plan had been lost.
      question: alreadyConnected
        ? `The student plan is already connected to this Creator account — ${account}. Confirm the connection again?`
        : `Connect the course student plan to this Creator account — ${account}?`,
      note: 'The link is permanent: this course account will not be able to activate the plan on another Creator account. If you did not press the button in the lesson, cancel.',
      confirm: 'Connect',
      cancel: 'Cancel',
    }
    : {
      question: alreadyConnected
        ? `Учебный тариф уже подключён к этому аккаунту Креатора — ${account}. Подтвердить подключение ещё раз?`
        : `Подключить учебный тариф курса к этому аккаунту Креатора — ${account}?`,
      note: 'Привязка постоянная: эта учётная запись курса больше не сможет активировать тариф на другом аккаунте. Если вы не нажимали кнопку в уроке, отмените.',
      confirm: 'Подключить',
      cancel: 'Отмена',
    };
}

// The backend answers each refusal with its own status; the page needs the
// reason, because only some of them are fixed by opening the lesson again.
export function courseClaimErrorCode(status: number): CourseClaimError {
  if (status === 409) return 'already_linked';
  if (status === 403) return 'not_enrolled';
  if (status === 400) return 'invalid_claim';
  return 'unavailable';
}

type MessageKey = CourseClaimError | Exclude<CourseClaimHandoff, 'pending'> | 'active' | 'unknown';

const RU_STALE_LINK = 'Ссылка активации устарела. Откройте бонусный урок курса и нажмите кнопку ещё раз.';
const EN_STALE_LINK = 'The activation link has expired. Open the bonus lesson of the course and press the button again.';

const messages: Record<'ru' | 'en', Record<MessageKey, string>> = {
  ru: {
    active: 'Студенческий доступ активирован.',
    already_linked: 'Эта учётная запись курса уже привязана к другому аккаунту Креатора. Войдите в тот аккаунт или напишите нам через «Обратную связь».',
    not_enrolled: 'Зачисление на курс неактивно, поэтому учебный тариф недоступен.',
    invalid_claim: RU_STALE_LINK,
    // A refused handoff: the same advice, only it arrives in the URL instead of a response.
    invalid: RU_STALE_LINK,
    expired: RU_STALE_LINK,
    unavailable: 'Не удалось проверить зачисление на курс. Попробуйте через несколько минут.',
    unknown: 'Не удалось активировать доступ. Откройте бонусный урок и попробуйте ещё раз.',
  },
  en: {
    active: 'Student access activated.',
    already_linked: 'This course account is already linked to another Creator account. Sign in to that account or contact us via Feedback.',
    not_enrolled: 'Your course enrollment is inactive, so the student plan is unavailable.',
    invalid_claim: EN_STALE_LINK,
    invalid: EN_STALE_LINK,
    expired: EN_STALE_LINK,
    unavailable: 'We could not verify your course enrollment. Please try again in a few minutes.',
    unknown: 'Activation failed. Open the bonus lesson and try again.',
  },
};

export function courseClaimResultMessage(result: string, lang: 'ru' | 'en'): string {
  const texts = messages[lang];
  return result in texts ? texts[result as keyof typeof texts] : texts.unknown;
}
